import { useState, useCallback, useRef } from "react";
import { apiV1 } from "api-v2";

import type { PastProject } from "newui/features/Company/types";

interface UseProjectsFilterResult {
	filteredProjects: PastProject[] | null;
	isFiltering: boolean;
	clearFilter: () => void;
	filterProjects: (search: string, sectorId?: number | null) => void;
}

export const useProjectsFilter = (
	companyId: number
): UseProjectsFilterResult => {
	const [filteredProjects, setFilteredProjects] =
		useState<PastProject[] | null>(null);
	const [isFiltering, setIsFiltering] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	const filterProjects = useCallback(
		async (search: string, sectorId?: number | null) => {
			// Cancel any in-flight request
			abortRef.current?.abort();
			abortRef.current = new AbortController();

			setIsFiltering(true);

			try {
				const params = new URLSearchParams({
					"filter[company_id]": String(companyId),
					"filter[name]": search,
					published: "true",
				});

				if (sectorId) {
					params.set("filter[sector_id]", String(sectorId));
				}

				const response = await apiV1.get(`past-projects?${params}`, {
					signal: abortRef.current.signal,
				});

				setFilteredProjects(response.data?.data ?? response.data);
			} catch (error: unknown) {
				if (error instanceof Error && error.name !== "AbortError") {
					console.error("Failed to filter projects:", error);
				}
			} finally {
				setIsFiltering(false);
			}
		},
		[companyId]
	);

	const clearFilter = useCallback(() => {
		abortRef.current?.abort();
		setFilteredProjects(null);
		setIsFiltering(false);
	}, []);

	return { filteredProjects, isFiltering, filterProjects, clearFilter };
};
